import { useEffect, useState } from 'react';
import { useEmployer } from '../context/EmployerContext';
import { useToast } from '../../toast/ToastContext';
import Pagination, { usePagination } from '../../icib-admin/components/Pagination';
import { fmtDT } from '../../icib-admin/utils/format';

const STATUS_FILTERS = ['all', 'pending', 'processed', 'failed', 'cancelled'];
const ACTION_FILTERS = ['all', 'ADDITION', 'UPDATE', 'REMOVAL'];

function productIds(v) {
  if (Array.isArray(v)) return v;
  return v ? [v] : [];
}

/* ICIB-13, employer_admin scope — view and cancel upcoming Additions,
   Updates and Removals for this employer_admin's own group. Same two
   endpoints as the ICIB admin console's PendingIntakeView
   (BrandonUserStoryAPI.md ICIB-13a/13b, README §2.6a/§4.4), which document
   `employer_admin` (own company only) alongside `icib_admin`. Scoping comes
   from EmployerContext's groupId — the scope selector only ever offers this
   employer_admin's assigned groups, and the server 403s any other company.

   An Addition/Removal submitted from Add Employee (EMP-03) or Remove
   Employee (EMP-04) with a future effective_date — or an Addition whose
   products' eligibility_days haven't passed yet — is held as a
   PendingIntakeRow until the daily sweep applies it on its `apply_on`
   date. This is where those rows are visible and where a still-`pending`
   one can be pulled back first. `processed`/`failed` rows already reflect
   something that happened, so only `pending` rows can be cancelled; a
   Removal for someone whose Addition was still waiting also cancels that
   Addition automatically, so `cancelled` doesn't always mean someone
   cancelled it here. Product names resolve from the group's (active-only,
   for this role) product list, falling back to the raw id. */
function PendingIntakeView() {
  const {
    status, groupId, curGroup, groupProductsIn,
    pendingIntake, pendingIntakeStatus, pendingIntakeError, loadPendingIntake, cancelPendingIntake,
  } = useEmployer();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { loadPendingIntake(groupId); }, [groupId, loadPendingIntake]);

  const products = groupId ? groupProductsIn(groupId) : [];
  const productName = (id) => products.find((p) => p.id === id)?.name || id;

  const rows = pendingIntake
    .filter((r) => statusFilter === 'all' || r.status === statusFilter)
    .filter((r) => actionFilter === 'all' || r.action === actionFilter)
    .filter((r) => {
      // Rows carry only an email to identify the person (no name), so
      // search matches that plus the product names shown in the table.
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      const names = productIds(r.subsidised_product_level).map(productName).join(' ');
      return `${r.email || ''} ${names}`.toLowerCase().includes(needle);
    });
  const { page, setPage, pageCount, pageItems: pagedRows, total } = usePagination(rows);

  async function onCancel(row) {
    setBusyId(row.pending_intake_row_id);
    const res = await cancelPendingIntake(groupId, row.pending_intake_row_id);
    setBusyId(null);
    if (res?.error) toast(res.message || 'Could not cancel this row.', 'bad', res.error);
    else toast('Cancelled.', 'ok');
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Scheduled enrolment</div>
      <div className="page-h"><h2>Pending enrolment</h2></div>
      <p className="page-sub">
        Additions, updates and removals for {curGroup()?.name || 'your employee benefits scheme'} that apply on a
        future date — a future effective date, or for an addition, a product whose eligibility period hasn't
        passed yet. Nothing happens until the "applies on" date, and a still-upcoming row can be
        pulled back first.
      </p>

      {pendingIntakeError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load pending enrolment</b>{pendingIntakeError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>No company assigned</b>Your account isn't currently assigned to a company.</div></div>
      ) : (
        <>
          <div className="filterbar">
            <input type="search" placeholder="Search email or product…" value={q} onChange={(e) => setQ(e.target.value)} />
            {STATUS_FILTERS.map((s) => (
              <span key={s} className="chip" aria-pressed={statusFilter === s} onClick={() => setStatusFilter(s)} role="button" tabIndex={0}>
                {s === 'all' ? 'All' : s}
              </span>
            ))}
            <span className="spacer" style={{ flex: 1 }} />
            {ACTION_FILTERS.map((a) => (
              <span key={a} className="chip" aria-pressed={actionFilter === a} onClick={() => setActionFilter(a)} role="button" tabIndex={0}>
                {a === 'all' ? 'All actions' : a}
              </span>
            ))}
            <span className="ep">GET /companies/{'{company_id}'}/pending-intake</span>
          </div>

          <div className="card">
            {pendingIntakeStatus === 'loading' || pendingIntakeStatus === 'idle' ? (
              <div className="empty"><b>Loading…</b></div>
            ) : rows.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Action</th><th>Person</th><th>Products</th><th>Applies on</th><th>Status</th><th>Effective date</th><th></th></tr></thead>
                  <tbody>
                    {pagedRows.map((r) => (
                      <tr key={r.pending_intake_row_id}>
                        <td><span className="tag navy">{r.action}</span></td>
                        <td><span className="strong">{r.email}</span></td>
                        <td>
                          {productIds(r.subsidised_product_level).length
                            ? productIds(r.subsidised_product_level).map(productName).join(', ')
                            : <span className="dim">—</span>}
                        </td>
                        <td className="mono nowrap">{r.apply_on || r.effective_date}</td>
                        <td>
                          <span className={'tag' + (r.status === 'processed' ? ' ok' : r.status === 'failed' ? ' bad' : r.status === 'cancelled' ? ' off' : ' warn')}>{r.status}</span>
                          {r.status === 'failed' && r.error_message && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{r.error_message}</div>}
                          {r.status === 'processed' && r.processed_at && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{fmtDT(r.processed_at)}</div>}
                          {r.status === 'cancelled' && r.cancelled_at && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{fmtDT(r.cancelled_at)}</div>}
                        </td>
                        <td className="mono nowrap">{r.effective_date || '—'}</td>
                        <td>
                          {r.status === 'pending' && (
                            <button className="btn sm danger" disabled={busyId === r.pending_intake_row_id} onClick={() => onCancel(r)}>
                              {busyId === r.pending_intake_row_id ? '…' : 'Cancel'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty"><b>Nothing here</b>{q.trim() ? 'Try a different search.' : 'No scheduled enrolment matches this filter.'}</div>}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </>
      )}
    </>
  );
}

export default PendingIntakeView;
